import {Button} from '@chekinapp/ui';
import {useState, useId} from 'react';
import {Controller, useFormContext, useWatch} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {EsimPackage} from '@chekin/core';
import {Counter, CounterSize} from 'components/common/Counter';
import {Fragment} from 'react/jsx-runtime';
import {ChevronDown, ShoppingCart, LoaderCircle} from 'lucide-react';
import {getPrice, getTotalPrice, hasItems} from './utils';
import styles from './styles.module.css';
import cn from 'clsx';

type Props = {
  currencySign?: string;
  isLoading: boolean;
  className?: string;
  esimPackages: EsimPackage[];
  formId: string;
  onChange: (id: string, onChange: (value: number) => void) => (value: number) => void;
};
export const MobileTotalToPayForm = ({
  className,
  esimPackages,
  formId,
  isLoading,
  currencySign,
  onChange,
}: Props) => {
  const {t} = useTranslation();
  const {
    control,
    formState: {isSubmitting},
  } = useFormContext();
  const values = useWatch({control});
  const isNotEmpty = hasItems(esimPackages)(values);
  const [isPriceDetailsOpen, setIsPriceDetailsOpen] = useState(true);
  const priceDetailsId = useId();

  return (
    <div
      className={cn(
        className,
        'rounded-none shadow-[0_-4px_6px_0_rgba(0,0,0,0.07)] flex flex-col gap-6 p-6 self-start bg-[var(--navigation-background)] ',
      )}
    >
      {isNotEmpty && (
        <div className="flex flex-col gap-4">
          <div className="flex items-center w-full justify-between gap-2">
            <button
              className="flex items-center gap-1 text-sm"
              onClick={() => setIsPriceDetailsOpen(open => !open)}
              aria-controls={priceDetailsId}
              aria-expanded={isPriceDetailsOpen}
            >
              <span className="font-medium">{t('esim.choose_plan.price_details')}</span>
              <ChevronDown
                size={16}
                className={cn({
                  'rotate-180': isPriceDetailsOpen,
                })}
              />
            </button>
            <div className="flex items-center gap-2">
              <div className="text-sm">{t('esim.choose_plan.total')}</div>
              <div className="font-semibold">
                {currencySign}
                {getTotalPrice(esimPackages)(values).toFixed(2)}
              </div>
            </div>
          </div>

          <div
            id={priceDetailsId}
            className={cn(
              'w-full grid gap-x-2 gap-y-4 grid-cols-3 [&>*:nth-child(3n+2)]:justify-self-center [&>*:nth-child(3n+3)]:justify-self-end max-h-24 overflow-y-auto',
              {
                hidden: !isPriceDetailsOpen,
              },
            )}
          >
            {esimPackages.map(
              esimPackage =>
                values[esimPackage.package_id]?.checked && (
                  <Fragment key={esimPackage.package_id}>
                    <div>
                      <div className="text-sm">{esimPackage.package_data}</div>
                      <div className="text-xs text-[var(--font-secondary)]">
                        {t('esim.choose_plan.valid_for_days', {
                          days: esimPackage.package_days,
                        })}
                      </div>
                    </div>

                    <Controller
                      name={`${esimPackage.package_id}.count`}
                      control={control}
                      render={({field}) => (
                        <Counter
                          className={styles.TotalToPay__counter}
                          value={field.value ?? 1}
                          onChange={onChange(esimPackage.package_id, field.onChange)}
                          size={CounterSize.S}
                        />
                      )}
                    />

                    <div>
                      {currencySign}
                      {getPrice(esimPackage, values[esimPackage.package_id]?.count)}
                    </div>
                  </Fragment>
                ),
            )}
          </div>
        </div>
      )}
      <Button
        form={formId}
        type="submit"
        disabled={!isNotEmpty || isLoading || isSubmitting}
        className="w-full flex items-center gap-2 text-base"
      >
        {isSubmitting ? (
          <LoaderCircle size={20} className="animate-spin" />
        ) : (
          <ShoppingCart size={20} className="[--lucide-stroke-width:3]" />
        )}

        {t('esim.choose_plan.buy_now')}
      </Button>
    </div>
  );
};
